/**
 * CSV-IMPORT-1 — brings in the clients setup is holding, once the plan
 * is live.
 *
 * Setup's import step comes before its plan step, and a business with
 * no plan yet is on Free (3 clients). So setup reads the file and holds
 * the clients on this device (services/clientImport.ts), and this —
 * mounted with the app in ProtectedRoute, beside setup and beside
 * everything after it — brings them in as soon as the server says
 * there's a plan: usually on "Welcome aboard", right after checkout.
 *
 * A plan with a client limit takes what fits; the rest stays held and
 * comes across by itself after an upgrade. Renders nothing.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOrgId } from '@/hooks/useOrgId';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from '@/hooks/use-toast';
import {
  clearPendingImport,
  importClients,
  loadPendingImport,
  savePendingImport,
} from '@/services/clientImport';

/** One run per account at a time, across every mount in the tab. */
const running = new Set<string>();

const people = (n: number) => `${n} client${n === 1 ? '' : 's'}`;

export function PendingClientImport() {
  const { user } = useAuth();
  const { data: orgId } = useOrgId();
  const { hasServerPlan, isLoading } = useSubscription();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  useEffect(() => {
    if (!userId || !orgId || isLoading || !hasServerPlan) return;
    const pending = loadPendingImport(userId);
    if (!pending || running.has(userId)) return;
    running.add(userId);
    void (async () => {
      try {
        const r = await importClients(orgId, pending.clients, { createdAt: pending.pickedAt });
        if (r.imported > 0) void queryClient.invalidateQueries({ queryKey: ['clients'] });
        if (r.leftover.length) {
          savePendingImport(userId, { ...pending, clients: r.leftover, toldAboutCap: true });
          // Say it once; after that, they come across quietly on an upgrade.
          if (r.imported > 0 || !pending.toldAboutCap) {
            toast({
              title: r.imported > 0 ? `${people(r.imported)} came across` : 'Your clients are waiting',
              description: `Your plan holds ${r.cap ?? 'a limited number of'} clients. Upgrade to bring the other ${
                r.leftover.length
              } — they'll come across on their own.`,
            });
          }
          return;
        }
        clearPendingImport(userId);
        if (r.imported > 0) {
          toast({
            title: 'Your clients came across',
            description: `${people(r.imported)} from ${pending.fileName} ${r.imported === 1 ? 'is' : 'are'} in your roster.${
              r.alreadyThere ? ` ${r.alreadyThere} ${r.alreadyThere === 1 ? 'was' : 'were'} already there.` : ''
            }`,
          });
        }
      } catch (err) {
        // Held clients stay held; the next time Orbit opens, it tries again.
        toast({
          title: "Your clients didn't come across yet",
          description: `We'll try again the next time you open Orbit. ${err instanceof Error ? err.message : ''}`.trim(),
          variant: 'destructive',
        });
      } finally {
        running.delete(userId);
      }
    })();
  }, [userId, orgId, isLoading, hasServerPlan, queryClient]);

  return null;
}

export default PendingClientImport;
